/** Mirrors `server/src/validation/schemas.js`. Keep the two in step. */

export const CUSTOMER_TYPES = ['Standard', 'Regular'];
export const ROLES = ['admin', 'vendor', 'customer', 'user'];
export const MIN_PASSWORD = 8;

const WORST_PASSWORDS = [
  'password', 'password1', 'password123', '12345678', '123456789', 'qwerty123',
  'admin123', 'welcome1', 'letmein1', 'iloveyou', 'changeme', 'changeme123',
];

export const passwordSpec = {
  label: 'Password', required: true, min: MIN_PASSWORD, max: 72,
  password: true, worstList: WORST_PASSWORDS,
};

export const loginSchema = {
  email: { label: 'Email ID', required: true, email: true, max: 120 },
  password: { label: 'Password', required: true, max: 72 },
};

export const registerSchema = {
  name: { label: 'Name', required: true, min: 2, max: 60 },
  email: { label: 'Email ID', required: true, email: true, max: 120 },
  phone1: { label: 'Phone number', required: true, phone: true },
  password: passwordSpec,
  confirmPassword: {
    label: 'Confirm password', required: true, max: 72,
    match: 'password', matchMessage: 'The two passwords do not match',
  },
};

export const changePasswordSchema = {
  currentPassword: { label: 'Current password', required: true, max: 72 },
  newPassword: passwordSpec,
  confirmPassword: {
    label: 'Confirm password', required: true, max: 72,
    match: 'newPassword', matchMessage: 'The two passwords do not match',
  },
};
export const MAX_QTY = 100000;
export const MAX_PRICE = 10000000;

export const catalogSchema = {
  name: { label: 'Name', required: true, min: 1, max: 60, safeName: true },
};

export const vendorSchema = {
  company: { label: 'Company name', required: true, min: 2, max: 80 },
  contact: { label: 'Contact person', max: 60 },
  phone: { label: 'Phone number', phone: true },
  address: { label: 'Address', max: 200 },
  gst: { label: 'GST number', gstin: true },
};

export const customerSchema = {
  company: { label: 'Customer name', required: true, min: 2, max: 80 },
  contact: { label: 'Contact person', max: 60 },
  phone: { label: 'Phone number', phone: true },
  address: { label: 'Address', max: 200 },
  gst: { label: 'GST number', gstin: true },
  customerType: { label: 'Customer type', required: true, oneOf: CUSTOMER_TYPES },
};

export const userSchema = {
  name: { label: 'Name', required: true, min: 2, max: 60 },
  email: { label: 'Email ID', required: true, email: true, max: 120 },
  phone1: { label: 'Phone number', required: true, phone: true },
  phone2: { label: 'Alternate phone', phone: true },
  role: { label: 'Role', required: true, oneOf: ROLES },
  modules: { label: 'Module', type: 'array', required: true },
};

/** Creating a user sets a password; editing one leaves it alone. */
export const userCreateSchema = { ...userSchema, password: passwordSpec };

export const stockInSchema = {
  vendor: { label: 'Vendor', required: false },
  date: { label: 'Date', required: true, date: true, notFuture: true },
};

/** A receipt is created in batches but corrected one row at a time. */
export const stockInEditSchema = {
  vendor: { label: 'Vendor', required: false, max: 80 },
  date: { label: 'Date', required: true, date: true, notFuture: true },
  product: { label: 'Product', required: true, max: 60 },
  texture: { label: 'Texture', required: true, max: 60 },
  length: { label: 'Length', required: true, max: 60 },
  color: { label: 'Color', required: true, max: 60 },
  qty: { label: 'Quantity', type: 'integer', required: true, min: 1, max: MAX_QTY },
};

export const stockInRowSchema = {
  product: { label: 'Product', required: true },
  texture: { label: 'Texture', required: true },
  length: { label: 'Length', required: true },
  color: { label: 'Color', required: true },
  qty: { label: 'Quantity', type: 'integer', required: true, min: 1, max: MAX_QTY },
};

export const stockOutSchema = {
  customer: { label: 'Customer', required: true },
  date: { label: 'Invoice date', required: true, date: true, notFuture: true },
};

/** Price and GST % are typed by hand; both optional (price is GST inclusive). */
export const stockOutLineSchema = {
  product: { label: 'Product', required: true },
  texture: { label: 'Texture', required: true },
  color: { label: 'Color', required: true },
  length: { label: 'Length', required: true },
  qty: { label: 'Quantity', type: 'integer', required: true, min: 1, max: MAX_QTY },
  price: { label: 'Price', type: 'number', required: false, min: 0, max: MAX_PRICE },
  gst: { label: 'GST %', type: 'number', required: false, min: 0, max: 100 },
};

export const priceRowSchema = {
  product: { label: 'Product', required: true },
  texture: { label: 'Texture', required: true },
  color: { label: 'Color', required: true },
  length: { label: 'Length', required: true },
  price: { label: 'Price', type: 'number', required: false, moreThan: 0, max: MAX_PRICE },
  gst: { label: 'GST %', type: 'number', min: 0, max: 100 },
};

/** Which fields carry a required marker in the UI. */
export function requiredFields(schema) {
  return new Set(Object.entries(schema).filter(([, spec]) => spec.required).map(([field]) => field));
}
